/**
 * Single-source shortcut registry (Vivarium styleguide §07).
 *
 * One place names every keyboard shortcut so the command palette's `<kbd>` chips
 * stay in lock-step with the real handlers in `app.tsx` (and the component-local
 * ⌘K / ⌘P). The chips render from this map via keysFor(id); the matching global
 * handlers live in `app.tsx`'s keydown (open-projects ⌘O, new-tsx ⇧⌘N,
 * new-conversation ⇧⌘K, zoom-fit ⇧1, plus editor/timeline/undo/etc). `match` is
 * the documented matcher; app.tsx mirrors it inline.
 *
 * Keys are display glyphs in the order they should render: ⇧ ⌥ ⌃ ⌘ then the
 * letter (matches macOS modifier order). `match` is optional metadata for the
 * eventual app.tsx refactor; it is NOT wired yet.
 *
 * IDs are the canonical shortcut ids. Where one matches a palette command id
 * (e.g. `open-projects`), the palette shows its chip automatically via keysFor().
 */
export interface KeyBinding {
  keys: string[];
  /** Optional matcher for a future app.tsx refactor. Not consumed yet. */
  match?: (e: KeyboardEvent) => boolean;
}

const meta = (e: KeyboardEvent) => e.metaKey || e.ctrlKey;
const letter = (e: KeyboardEvent, k: string) => e.key.toLowerCase() === k;

export const KEYMAP: Record<string, KeyBinding> = {
  'editor-panel':   { keys: ['⌘', 'E'], match: (e) => meta(e) && !e.shiftKey && !e.altKey && letter(e, 'e') },
  'motion-timeline':{ keys: ['⌘', 'T'], match: (e) => meta(e) && !e.shiftKey && !e.altKey && letter(e, 't') },
  'undo':           { keys: ['⌘', 'Z'], match: (e) => meta(e) && !e.shiftKey && !e.altKey && letter(e, 'z') },
  'redo':           { keys: ['⇧', '⌘', 'Z'], match: (e) => meta(e) && e.shiftKey && letter(e, 'z') }, // ⌘Y also bound in app.tsx
  'select-all':     { keys: ['⌘', 'A'], match: (e) => meta(e) && !e.shiftKey && !e.altKey && letter(e, 'a') },
  'duplicate':      { keys: ['⌘', 'D'], match: (e) => meta(e) && !e.shiftKey && !e.altKey && letter(e, 'd') },
  'open-projects':  { keys: ['⌘', 'O'], match: (e) => meta(e) && !e.shiftKey && !e.altKey && letter(e, 'o') },
  'command-menu':   { keys: ['⌘', 'P'], match: (e) => meta(e) && letter(e, 'p') },
  'ask-agent':      { keys: ['⌘', 'K'], match: (e) => meta(e) && !e.shiftKey && letter(e, 'k') },
  'new-tsx':        { keys: ['⇧', '⌘', 'N'], match: (e) => meta(e) && e.shiftKey && letter(e, 'n') },
  'new-conversation': { keys: ['⇧', '⌘', 'K'], match: (e) => meta(e) && e.shiftKey && letter(e, 'k') },
  'zoom-fit':       { keys: ['⇧', '1'], match: (e) => e.shiftKey && !meta(e) && !e.altKey && e.code === 'Digit1' },
};

/** Display glyphs for a command/shortcut id, or [] when unbound. */
export function keysFor(id: string): string[] {
  return KEYMAP[id]?.keys ?? [];
}
